// Contract interfaces and types for the Fakewhale platform

export interface FactoryAddresses {
  network: string;
  chainId: number;
  deployer: string;
  factories: {
    userManager: string;
    proofOfCuration: string;
    openCollectionFactory: string;
    closedCollectionFactory: string;
    directSaleFactory: string;
    auctionSaleFactory: string;
    directSalePieceFactory: string;
    auctionSalePieceFactory: string;
    fakeERC20: string;
  };
  timestamp: string;
  deploymentInfo?: {
    blockNumber?: number;
    gasUsed?: number;
  };
}

export interface Collection {
  address: string;
  type: "open" | "closed";
  name: string;
}

export interface Sale {
  address: string;
  type: "direct" | "auction" | "direct-piece" | "auction-piece";
  collectionAddress: string;
  collectionName?: string;
  collectionType?: "open" | "closed";
}

export interface UserManagerInfo {
  allowedUsers: string[];
  contractManagers: { [userAddress: string]: string };
  userCount: number;
}

export interface SaleInfo {
  isActive: boolean;
  price: string;
  paymentReceiver: string;
  paymentReceiverBalance: string;
  devFeeRecipient: string;
  devFeeRecipientBalance: string;
  devFeePercentage: number;
  fakewhaleFeeRecipient: string;
  fakewhaleFeeRecipientBalance: string;
  fakewhaleFeePercentage: number;
  contractBalance: string;
  isERC20Sale?: boolean;
  isERC20Auction?: boolean;
  erc20Token?: string;
  tokenSymbol?: string;
  tokenName?: string;
  tokenDecimals?: number;
  // Auction specific
  currentBid?: string;
  highestBidder?: string;
  timeRemaining?: number;
}

export interface CollectionInfo {
  totalSupply: string;
  maxSupply: string;
  name: string;
  symbol: string;
  baseURI: string;
  contractLocked: boolean;
  lockTimestamp: number;
  owner: string;
}

export interface TokenMetadata {
  tokenId: number;
  tokenURI: string;
  metadata: any | null;
  error: string | null;
}

// ABI definitions for contract interactions
export const USER_MANAGER_ABI = [
  "function manageUser(address _user, bool _allowed, address _contractManager) external",
  "function removeUser(address _user) external",
  "function isUserAllowed(address _user) external view returns (bool)",
  "function contractManagerForUser(address _user) external view returns (address)",
  "function getAllowedUsers() external view returns (address[])",
  "function getAllowedUsersCount() external view returns (uint256)",
  "function getAllowedUsersPaginated(uint256 _offset, uint256 _limit) external view returns (address[])",
  "function batchManageUsers(address[] calldata _users, bool _allowed) external",
  "event UserAllowed(address indexed user, bool allowed, address indexed contractManager)",
  "event UserRemoved(address indexed user)",
];

export const COLLECTION_ABI = [
  "function baseURI() public view returns (string)",
  "function totalSupply() public view returns (uint256)",
  "function contractLocked() public view returns (bool)",
  "function lockTimestamp() public view returns (uint256)",
  "function owner() public view returns (address)",
  "function allowedMinters(address) public view returns (bool)",
  "function userManager() public view returns (address)",
  "function name() external view returns (string)",
  "function symbol() external view returns (string)",
  "function tokenURI(uint256 _tokenId) public view returns (string)",
  "function setBaseURI(string memory _baseURI) public",
  "function lockContract() public",
  "function revertLock() public",
  "function manageMinter(address _minter, bool _allowed) public",
];

export const CLOSED_COLLECTION_ABI = [
  ...COLLECTION_ABI,
  "function maxSupply() public view returns (uint256)",
  "function setMaxSupply(uint256 _maxSupply) public",
];

// Base NAME function for contract type detection
export const NAME_ABI = [
  "function NAME() external view returns (string)",
  "function VERSION() external view returns (string)",
];

// FakewhaleDirectSale - Global sale, no token selection
export const DIRECT_SALE_ABI = [
  "function NAME() external view returns (string)",
  "function VERSION() external view returns (string)",
  "function salePrice() external view returns (uint256)",
  "function saleActive() external view returns (bool)",
  "function paymentReceiver() external view returns (address)",
  "function devFeeRecipient() external view returns (address)",
  "function devFeePercentage() external view returns (uint256)",
  "function fakewhaleFeeRecipient() external view returns (address)",
  "function fakewhaleFeePercentage() external view returns (uint256)",
  "function isERC20Sale() external view returns (bool)",
  "function erc20Token() external view returns (address)",
  "function tokenSymbol() external view returns (string)",
  "function tokenName() external view returns (string)",
  "function tokenDecimals() external view returns (uint8)",
  "function buy() external payable",
  "function startSale() external",
  "function stopSale() external",
  "function emergencyWithdraw() external",
  "function presaleActive() external view returns (bool)",
  "function whitelistMintLimit(address) external view returns (uint256)",
  "function whitelistMintCount(address) external view returns (uint256)",
  "function startPresale() external",
  "function stopPresale() external",
  "function addToWhitelist(address _user, uint256 _mintLimit) external",
  "function removeFromWhitelist(address _user) external",
  "function addToWhitelistBatch(address[] calldata _users, uint256[] calldata _mintLimits) external",
];

// FakewhaleDirectSalePiece - Sale with token selection
export const DIRECT_SALE_PIECE_ABI = [
  "function NAME() external view returns (string)",
  "function VERSION() external view returns (string)",
  "function salePrice() external view returns (uint256)",
  "function saleActive() external view returns (bool)",
  "function paymentReceiver() external view returns (address)",
  "function devFeeRecipient() external view returns (address)",
  "function devFeePercentage() external view returns (uint256)",
  "function fakewhaleFeeRecipient() external view returns (address)",
  "function fakewhaleFeePercentage() external view returns (uint256)",
  "function isERC20Sale() external view returns (bool)",
  "function erc20Token() external view returns (address)",
  "function tokenSymbol() external view returns (string)",
  "function tokenName() external view returns (string)",
  "function tokenDecimals() external view returns (uint8)",
  "function buy(uint256 _tokenId) external payable",
  "function startSale() external",
  "function stopSale() external",
  "function emergencyWithdraw() external",
  "function tokenPresaleActive(uint256) external view returns (bool)",
  "function tokenWhitelisted(uint256, address) external view returns (bool)",
  "function tokenWhitelistMinted(uint256, address) external view returns (bool)",
  "function startTokenPresale(uint256 _tokenId) external",
  "function stopTokenPresale(uint256 _tokenId) external",
  "function addToTokenWhitelist(uint256 _tokenId, address _user) external",
  "function removeFromTokenWhitelist(uint256 _tokenId, address _user) external",
  "function addToTokenWhitelistBatch(uint256 _tokenId, address[] calldata _users) external",
];

// FakewhaleAuctionSale - Global auction, no token selection
export const AUCTION_SALE_ABI = [
  "function NAME() external view returns (string)",
  "function VERSION() external view returns (string)",
  "function startingPrice() external view returns (uint256)",
  "function auctionActive() external view returns (bool)",
  "function currentHighestBid() external view returns (uint256)",
  "function currentHighestBidder() external view returns (address)",
  "function paymentReceiver() external view returns (address)",
  "function devFeeRecipient() external view returns (address)",
  "function devFeePercentage() external view returns (uint256)",
  "function fakewhaleFeeRecipient() external view returns (address)",
  "function fakewhaleFeePercentage() external view returns (uint256)",
  "function getTimeRemaining() external view returns (uint256)",
  "function isERC20Auction() external view returns (bool)",
  "function erc20Token() external view returns (address)",
  "function tokenSymbol() external view returns (string)",
  "function tokenName() external view returns (string)",
  "function tokenDecimals() external view returns (uint8)",
  "function placeBid() external payable",
  "function withdrawBid() external",
  "function startAuction(uint256 _auctionPeriod) external",
  "function stopAuction() external",
  "function endAuction(bool _force) external",
  "function presaleActive() external view returns (bool)",
  "function whitelistBidLimit(address) external view returns (uint256)",
  "function whitelistBidCount(address) external view returns (uint256)",
  "function startPresale() external",
  "function stopPresale() external",
  "function addToWhitelist(address _user, uint256 _bidLimit) external",
  "function removeFromWhitelist(address _user) external",
  "function addToWhitelistBatch(address[] calldata _users, uint256[] calldata _bidLimits) external",
];

// FakewhaleAuctionSalePiece - Auction with token selection
export const AUCTION_SALE_PIECE_ABI = [
  "function NAME() external view returns (string)",
  "function VERSION() external view returns (string)",
  "function startingPrice() external view returns (uint256)",
  "function auctionActive() external view returns (bool)",
  "function currentHighestBid(uint256) external view returns (uint256)",
  "function currentHighestBidder(uint256) external view returns (address)",
  "function paymentReceiver() external view returns (address)",
  "function devFeeRecipient() external view returns (address)",
  "function devFeePercentage() external view returns (uint256)",
  "function fakewhaleFeeRecipient() external view returns (address)",
  "function fakewhaleFeePercentage() external view returns (uint256)",
  "function getTimeRemaining() external view returns (uint256)",
  "function isERC20Auction() external view returns (bool)",
  "function erc20Token() external view returns (address)",
  "function tokenSymbol() external view returns (string)",
  "function tokenName() external view returns (string)",
  "function tokenDecimals() external view returns (uint8)",
  "function placeBid(uint256 _tokenId) external payable",
  "function withdrawBid(uint256 _tokenId) external",
  "function startAuction(uint256 _auctionPeriod) external",
  "function stopAuction() external",
  "function endAuction(uint256 _tokenId, bool _force) external",
  "function tokenPresaleActive(uint256) external view returns (bool)",
  "function tokenWhitelisted(uint256, address) external view returns (bool)",
  "function tokenWhitelistBidPlaced(uint256, address) external view returns (bool)",
  "function startTokenPresale(uint256 _tokenId) external",
  "function stopTokenPresale(uint256 _tokenId) external",
  "function addToTokenWhitelist(uint256 _tokenId, address _user) external",
  "function removeFromTokenWhitelist(uint256 _tokenId, address _user) external",
  "function addToTokenWhitelistBatch(uint256 _tokenId, address[] calldata _users) external",
];

export const ERC20_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)",
  "function name() external view returns (string)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function transfer(address to, uint256 amount) external returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) external returns (bool)",
];

export const FACTORY_ABI = [
  "function getDeployedContracts(address _owner) external view returns (address[])",
  "function getAllDeployedContracts() external view returns (address[])",
  "function createFakewhaleOpenCollection(string memory _name, string memory _ticker, address _owner) external returns (address)",
  "function createFakewhaleClosedCollection(string memory _name, string memory _ticker, address _owner, uint256 _maxSupply) external returns (address)",
  "function createFakewhaleDirectSale(address _collectionContract, uint256 _salePrice, address _paymentReceiver, address _owner, address _erc20Token) external returns (address)",
  "function createFakewhaleAuctionSale(address _collectionContract, uint256 _startingPrice, address _paymentReceiver, address _owner, address _erc20Token) external returns (address)",
  "function createFakewhaleDirectSale(address _collectionContract, uint256 _salePrice, address _paymentReceiver, address _owner, address _erc20Token) external returns (address)",
  "function createFakewhaleAuctionSale(address _collectionContract, uint256 _startingPrice, address _paymentReceiver, address _owner, address _erc20Token) external returns (address)",
];
